import { useMemo, useState } from 'react';
import { Domicilio } from '../../model/Domicilio';
import { CanjeAction, CanjeStatus, useCanjeStatus } from './useCanjeStatus';
import { ProductoData, TipoProducto } from '../../model/ProductoData';
import { useCanjeCancel } from './useCanjeCancel';
import { useCustomerData } from './useCustomerData';
import { useCanjeRequests } from './useCanjeRequests';
import { DataOrigin, useDataInputSelect } from './useDataInputSelect';
import { Phone } from '../../model/Phone';
import { validateOTP } from './FormularioOtp';
import { useSmiles } from './useSmiles';
import { useFocusFirst } from './useFocusFirst';
import { ERROR_OTP_HTTP_CODE } from '../../rest/responseCodes';
import { useValidateProductType } from './useValidateProductType';
import { useUserStatus } from '../../config/authentication/useUserStatus';
import { OpenCanjeFn } from './useCanjeActivation';
import { useCheckSession } from './useCheckSession';

const DEFAULT_ADDRESS_TYPE = DataOrigin.SELECT_DATA;
const DEFAULT_PHONE_TYPE = DataOrigin.SELECT_DATA;

export function useCanje(
  show: boolean,
  addressKey: string,
  phoneKey: string,
  close?: () => void,
  product?: ProductoData,
  reopenCanje?: OpenCanjeFn,
  updateStock?: () => void
) {
  const { loginData, checkLogin } = useUserStatus();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [status, dispatch, isLoading, isError, isSuccessful, isCancellable, showOTP] = useCanjeStatus();
  const [existingAddresses, setExistingAddresses] = useState<Domicilio[]>();
  const [existingPhones, setExistingPhones] = useState<Phone[]>();

  const [addressFormData, addressValidate, resetAddress] = useDataInputSelect<Domicilio>(
    DEFAULT_ADDRESS_TYPE,
    existingAddresses,
    addressKey
  );

  const [phoneFormData, phoneValidate, resetPhone] = useDataInputSelect<Phone>(DEFAULT_PHONE_TYPE, existingPhones, phoneKey, Phone.parse);

  const [otp, setOtp] = useState<string>();
  const [smilesData, smilesValidate, resetSmiles] = useSmiles();

  const cancelCanje = useCanjeCancel(loginData);
  useValidateProductType(product, dispatch);

  const validate = async () => {
    switch (status) {
      case CanjeStatus.FORMULARIO: {
        return (
          (product?.tipoProducto !== TipoProducto.FISICO || (await addressValidate())) &&
          (product?.tipoProducto !== TipoProducto.SMILES || (await smilesValidate())) &&
          (await phoneValidate())
        );
      }
      case CanjeStatus.ERROR_OTP_STATUS:
      case CanjeStatus.OTP: {
        return validateOTP(otp);
      }
    }
    return false;
  };

  //Errores en las respuestas de backend
  const onError = useMemo(
    () => (reason?: any, errorCode?: number) => {
      setErrorMessage(reason);
      /** Se controla si el código de error es por OTP inválido*/
      if (showOTP && errorCode === ERROR_OTP_HTTP_CODE) {
        dispatch(CanjeAction.ERROR_OTP);
      } else {
        dispatch(CanjeAction.ERROR);
        /** Se anula el proceso de canje en los
         *   demás casos.
         */
        cancelCanje();
      }
    },
    [dispatch, cancelCanje, showOTP]
  );

  const sendForm = useCanjeRequests(
    dispatch,
    loginData,
    show,
    status,
    phoneFormData.getValue,
    addressFormData.getValue,
    addressFormData.dataOrigin,
    otp,
    product,
    smilesData.getValue,
    onError,
    updateStock
  );

  useCustomerData(setExistingAddresses, setExistingPhones, dispatch, loginData, show, status);

  const [firstElementRef, resetRef] = useFocusFirst(status, isLoading);

  function reset() {
    resetAddress();
    setOtp(undefined);
    resetSmiles();
    resetPhone();
    resetRef();
    setExistingAddresses([]);
    setExistingPhones([]);
    dispatch(CanjeAction.REINICIAR);
  }

  const checkSession = useCheckSession(checkLogin, reset, product, reopenCanje, close);

  async function onConfirm() {
    if (!(await validate())) return;

    /** Si se vence el token de sesión, se cierra el modal
     * de canje y se abre el modal de login.
     */
    if (!checkSession()) return;

    switch (status) {
      case CanjeStatus.FORMULARIO: {
        dispatch(CanjeAction.CONTINUAR);
        break;
      }
      case CanjeStatus.ERROR_OTP_STATUS:
      case CanjeStatus.OTP: {
        sendForm();
        setOtp(undefined);
        break;
      }
      default: {
      }
    }
  }

  function onCloseHandler() {
    if (isCancellable || isSuccessful || isError) {
      close?.();
      reset();
    }
    if (isCancellable) cancelCanje();
  }

  return [
    status,
    isLoading,
    isError,
    isSuccessful,
    showOTP,
    errorMessage,
    addressFormData,
    onCloseHandler,
    otp,
    setOtp,
    phoneFormData,
    smilesData,
    existingAddresses,
    existingPhones,
    onConfirm,
    firstElementRef,
  ] as const;
}
